import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// ✅ Color schema: Services = green, Goods = orange
const COLORS = {
  Goods: "#fb8c00", // Towary -> orange
  Services: "#2e7d32", // Usługi -> green
};

// more robust parser (handles "1 234,56", "1234.56", etc.)
const parseValue = (value) => {
  if (value == null || value === "") return 0;
  const cleaned = String(value)
    .replace(/\s/g, "") // remove spaces
    .replace(",", ".") // decimal comma -> dot
    .replace(/[^\d.-]/g, ""); // keep only digits, dot, minus

  const num = parseFloat(cleaned);
  return isNaN(num) || num < 0 ? 0 : num;
};

const formatNumber = (value) => {
  const num = parseFloat(value || 0);
  return num
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const months = [
  { value: 0, label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

// === Styled Components ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ContractorsComparisonByTransactionType = ({ salesData = [] }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);

  const availableYears = useMemo(() => {
    return [...new Set(salesData.map((row) => row.Rok?.toString()))].sort();
  }, [salesData]);

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(
        availableYears.includes("2024") ? "2024" : availableYears[0]
      );
    }
  }, [availableYears, selectedYear]);

  const topContractors = useMemo(() => {
    if (!selectedYear) return [];

    const contractorTotals = {};

    salesData
      .filter(
        (row) =>
          row.Rok?.toString() === selectedYear &&
          (selectedMonth === 0 ||
            parseInt(row.Miesiac, 10) === selectedMonth)
      )
      .forEach((row) => {
        const contractorID = row.NrKontrahenta?.trim();
        const contractorName = row.NazwaKontrahenta?.trim();
        if (!contractorID || !contractorName) return;

        if (!contractorTotals[contractorID]) {
          contractorTotals[contractorID] = {
            name: contractorName,
            Goods: 0,
            Services: 0,
          };
        }

        // === RULES aligned with SpzedazData ===
        // Services: K_12 (POZK2, EU, Service = "✓")
        const servicesAmount = parseValue(row.K_12);

        // Goods: all other K_ fields used for Amount in SpzedazData
        // (K_15, K_19, K_10, K_21, K_17, K_11, K_22, K_13, K_14)
        const goodsAmount =
          parseValue(row.K_15) +
          parseValue(row.K_19) +
          parseValue(row.K_10) +
          parseValue(row.K_21) +
          parseValue(row.K_17) +
          parseValue(row.K_11) +
          parseValue(row.K_22) +
          parseValue(row.K_13) +
          parseValue(row.K_14);

        contractorTotals[contractorID].Goods += goodsAmount;
        contractorTotals[contractorID].Services += servicesAmount;
      });

    return Object.entries(contractorTotals)
      .map(([contractorID, values]) => ({ contractorID, ...values }))
      .sort((a, b) => {
        const totalA = a.Goods + a.Services;
        const totalB = b.Goods + b.Services;
        return totalB - totalA;
      })
      .slice(0, 10);
  }, [salesData, selectedYear, selectedMonth]);

  const chartData = {
    labels: topContractors.map((item) => item.name),
    datasets: [
      {
        label: "Towary",
        data: topContractors.map((item) => item.Goods),
        backgroundColor: COLORS.Goods,
      },
      {
        label: "Usługi",
        data: topContractors.map((item) => item.Services),
        backgroundColor: COLORS.Services,
      },
    ],
  };

  if (!selectedYear || availableYears.length === 0) {
    return <div style={{ textAlign: "center" }}>Ładowanie danych...</div>;
  }

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Porównanie kontrahentów wg typów transakcji
        <InfoIconWrapper data-tooltip-id="contractors-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="contractors-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Porównanie kontrahentów</strong>
            <br />
            Wykres pokazuje top 10 kontrahentów
            <br />
            z podziałem na towary (pomarańczowy) i usługi (zielony).
          </>
        }
      />

      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <label style={{ fontWeight: 500 }}>Rok:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <label style={{ fontWeight: 500 }}>Miesiąc:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
            style={{
              padding: "8px 12px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: "1000px", margin: "0 auto" }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${formatNumber(
                      context.raw
                    )}`,
                },
              },
            },
            scales: {
              x: { stacked: false },
              y: {
                stacked: false,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" },
                },
                ticks: {
                  callback: (value) => formatNumber(value),
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default ContractorsComparisonByTransactionType;
